import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  createTelegramPublicationRule,
  deleteTelegramPublicationRule,
  fetchTelegramPublicationRules,
  fetchTelegramPublicationRulesMetadata,
  updateTelegramPublicationRule,
} from '../../api/adminTelegramPublicationRules';

const ALL_VALUE = '';

const PATTERN_FAMILY = 'PATTERN';
const TRADE_FAMILY = 'TRADE';

const EMPTY_FORM = {
  ruleFamily: PATTERN_FAMILY,
  destination: '',
  engineVersion: '',
  patternType: ALL_VALUE,
  direction: ALL_VALUE,
  timeframe: ALL_VALUE,
  minPatternScore: 70,
  maxPatternScore: '',
  eventMode: ALL_VALUE,
  eventType: ALL_VALUE,
  enabled: true,
};

const NO_MAX_VALUE = '';

function optionalScore(value) {
  if (value === null || value === undefined || value === NO_MAX_VALUE) {
    return null;
  }
  return Number(value);
}

function isTradeForm(form) {
  return form.ruleFamily === TRADE_FAMILY;
}

function toApiPayload(form) {
  const trade = isTradeForm(form);
  return {
    destination: form.destination,
    engineVersion: trade ? null : form.engineVersion,
    patternType: trade || form.patternType === ALL_VALUE ? null : form.patternType,
    direction: trade || form.direction === ALL_VALUE ? null : form.direction,
    timeframe: trade || form.timeframe === ALL_VALUE ? null : form.timeframe,
    minPatternScore: trade ? 0 : Number(form.minPatternScore),
    maxPatternScore: trade ? null : optionalScore(form.maxPatternScore),
    ruleFamily: form.ruleFamily,
    eventMode: trade && form.eventMode !== ALL_VALUE ? form.eventMode : null,
    eventType: trade && form.eventType !== ALL_VALUE ? form.eventType : null,
    enabled: form.enabled,
  };
}

function toFormPayload(rule) {
  return {
    ruleFamily: rule.ruleFamily || PATTERN_FAMILY,
    destination: rule.destination || '',
    engineVersion: rule.engineVersion || '',
    patternType: rule.patternType || ALL_VALUE,
    direction: rule.direction || ALL_VALUE,
    timeframe: rule.timeframe || ALL_VALUE,
    minPatternScore: rule.minPatternScore ?? 70,
    maxPatternScore: rule.maxPatternScore ?? '',
    eventMode: rule.eventMode || ALL_VALUE,
    eventType: rule.eventType || ALL_VALUE,
    enabled: rule.enabled !== false,
  };
}

function displayMax(value) {
  if (value === null || value === undefined || value === NO_MAX_VALUE) {
    return 'Aucun';
  }
  return value;
}

function display(value) {
  if (value === null || value === undefined || value === ALL_VALUE) {
    return 'ALL';
  }
  return value;
}

function isTradeRule(rule) {
  return (rule?.ruleFamily || PATTERN_FAMILY) === TRADE_FAMILY;
}

function displayEvent(rule) {
  if (!isTradeRule(rule)) {
    return '—';
  }
  return `${rule.eventMode || 'ALL'} / ${rule.eventType || 'ALL'}`;
}

const TelegramPublicationRulesAdminPage = () => {
  const [rules, setRules] = useState([]);
  const [metadata, setMetadata] = useState({ destinations: [], engines: [], patternTypes: [], directions: [], timeframes: [], ruleFamilies: [], tradingModes: [], tradingEventTypes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ruleList, ruleMetadata] = await Promise.all([
        fetchTelegramPublicationRules(),
        fetchTelegramPublicationRulesMetadata(),
      ]);
      setRules(Array.isArray(ruleList) ? ruleList : []);
      setMetadata(ruleMetadata || { destinations: [], engines: [], patternTypes: [], directions: [], timeframes: [], ruleFamilies: [], tradingModes: [], tradingEventTypes: [] });
    } catch (loadError) {
      setRules([]);
      setError(loadError.message || 'Impossible de charger les règles de publication Telegram.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enabledByDestination = useMemo(() => {
    const counts = {};
    rules.forEach((rule) => {
      if (rule.enabled !== false) {
        counts[rule.destination] = (counts[rule.destination] || 0) + 1;
      }
    });
    return counts;
  }, [rules]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setDialog({ mode: 'create' });
  };

  const openEdit = (rule) => {
    setForm(toFormPayload(rule));
    setFormError('');
    setDialog({ mode: 'edit', rule });
  };

  const closeDialog = () => {
    setDialog(null);
    setFormError('');
  };

  const validate = () => {
    if (!form.destination) {
      return 'La destination est requise.';
    }
    if (isTradeForm(form)) {
      if (form.destination !== TRADE_FAMILY) {
        return 'Une règle TRADE doit utiliser la destination TRADE.';
      }
      return '';
    }
    if (!form.engineVersion) {
      return 'Le moteur pattern est requis.';
    }
    const score = Number(form.minPatternScore);
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      return 'Le score minimum doit être un entier entre 0 et 100.';
    }
    const hasMax = form.maxPatternScore !== NO_MAX_VALUE
      && form.maxPatternScore !== null
      && form.maxPatternScore !== undefined;
    if (hasMax) {
      const max = Number(form.maxPatternScore);
      if (!Number.isFinite(max) || max < 0 || max > 100) {
        return 'Le score maximum doit être un entier entre 0 et 100.';
      }
      if (max < score) {
        return 'Le score maximum doit être supérieur ou égal au score minimum.';
      }
    }
    return '';
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError('');
    try {
      const payload = toApiPayload(form);
      if (dialog?.mode === 'edit') {
        await updateTelegramPublicationRule(dialog.rule.id, payload);
      } else {
        await createTelegramPublicationRule(payload);
      }
      closeDialog();
      setSnackbar('Règle enregistrée.');
      await load();
    } catch (submitError) {
      setFormError(submitError.message || 'Impossible d’enregistrer la règle.');
    }
  };

  const toggleEnabled = async (rule) => {
    const next = !(rule.enabled !== false);
    const isLast = next === false && (enabledByDestination[rule.destination] || 0) <= 1;
    if (isLast && !window.confirm(
      'Aucune règle active ne restera pour cette destination. Les nouvelles publications Telegram vers cette destination seront bloquées.',
    )) {
      return;
    }
    try {
      const payload = toApiPayload({
        ...toFormPayload(rule),
        enabled: next,
        minPatternScore: rule.minPatternScore ?? 70,
      });
      await updateTelegramPublicationRule(rule.id, payload);
      setSnackbar(next ? 'Règle activée.' : 'Règle désactivée.');
      await load();
    } catch (toggleError) {
      setError(toggleError.message || 'Impossible de modifier la règle.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await deleteTelegramPublicationRule(deleteTarget.id);
      setDeleteTarget(null);
      setSnackbar('Règle supprimée.');
      await load();
    } catch (deleteError) {
      setError(deleteError.message || 'Impossible de supprimer la règle.');
    }
  };

  const requestDelete = (rule) => {
    const isLast = (enabledByDestination[rule.destination] || 0) <= 1 && rule.enabled !== false;
    setDeleteTarget({ ...rule, isLast });
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Telegram" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Règles de publication Telegram
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Détermine quels patterns sont publiés vers quelles destinations. Aucune règle active pour une destination = aucune publication Telegram vers cette destination.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}
              sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, '&:hover': { bgcolor: '#e6b94f' }, alignSelf: 'flex-start' }}>
              Ajouter une règle
            </Button>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: '#4f646f' }}>Chargement des règles...</Typography>
            </Box>
          ) : rules.length === 0 ? (
            <Alert severity="warning">
              Aucune règle active = aucune publication Telegram. Aucune règle n’est configurée.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Enabled</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Destination</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Famille</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Engine</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Direction</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Timeframe</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Score min</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Score max</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Événement</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id} hover>
                      <TableCell>
                        <Switch size="small" checked={rule.enabled !== false} onChange={() => toggleEnabled(rule)} />
                      </TableCell>
                      <TableCell>{rule.destination}</TableCell>
                      <TableCell>{rule.ruleFamily || PATTERN_FAMILY}</TableCell>
                      <TableCell>{isTradeRule(rule) ? '—' : display(rule.engineVersion)}</TableCell>
                      <TableCell>{isTradeRule(rule) ? '—' : display(rule.patternType)}</TableCell>
                      <TableCell>{isTradeRule(rule) ? '—' : display(rule.direction)}</TableCell>
                      <TableCell>{isTradeRule(rule) ? '—' : display(rule.timeframe)}</TableCell>
                      <TableCell>{isTradeRule(rule) ? '—' : rule.minPatternScore}</TableCell>
                      <TableCell>{isTradeRule(rule) ? '—' : displayMax(rule.maxPatternScore)}</TableCell>
                      <TableCell>{displayEvent(rule)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => openEdit(rule)}>Modifier</Button>
                          <Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => requestDelete(rule)}>Supprimer</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialog?.mode === 'edit' ? 'Modifier la règle' : 'Ajouter une règle'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Famille *</InputLabel>
              <Select label="Famille *" value={form.ruleFamily}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  ruleFamily: e.target.value,
                  destination: e.target.value === TRADE_FAMILY ? 'TRADE' : '',
                  engineVersion: e.target.value === TRADE_FAMILY ? '' : f.engineVersion,
                }))}>
                {(metadata.ruleFamilies?.length ? metadata.ruleFamilies : [PATTERN_FAMILY, TRADE_FAMILY])
                  .map((family) => <MenuItem key={family} value={family}>{family}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Destination *</InputLabel>
              <Select label="Destination *" value={form.destination} onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}>
                {metadata.destinations.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>

            {isTradeForm(form) ? (
              <>
                <FormControl fullWidth>
                  <InputLabel>Mode (SHADOW / REAL)</InputLabel>
                  <Select label="Mode (SHADOW / REAL)" value={form.eventMode} onChange={(e) => setForm((f) => ({ ...f, eventMode: e.target.value }))}>
                    <MenuItem value={ALL_VALUE}>ALL</MenuItem>
                    {(metadata.tradingModes || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Type d&apos;événement</InputLabel>
                  <Select label="Type d'événement" value={form.eventType} onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}>
                    <MenuItem value={ALL_VALUE}>ALL</MenuItem>
                    {(metadata.tradingEventTypes || []).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>Moteur (Engine) *</InputLabel>
                  <Select label="Moteur (Engine) *" value={form.engineVersion} onChange={(e) => setForm((f) => ({ ...f, engineVersion: e.target.value }))}>
                    {metadata.engines.map((engine) => <MenuItem key={engine} value={engine}>{engine}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Pattern</InputLabel>
                  <Select label="Pattern" value={form.patternType} onChange={(e) => setForm((f) => ({ ...f, patternType: e.target.value }))}>
                    <MenuItem value={ALL_VALUE}>ALL</MenuItem>
                    {metadata.patternTypes.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Direction</InputLabel>
                  <Select label="Direction" value={form.direction} onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))}>
                    <MenuItem value={ALL_VALUE}>ALL</MenuItem>
                    {metadata.directions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Timeframe</InputLabel>
                  <Select label="Timeframe" value={form.timeframe} onChange={(e) => setForm((f) => ({ ...f, timeframe: e.target.value }))}>
                    <MenuItem value={ALL_VALUE}>ALL</MenuItem>
                    {metadata.timeframes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>

                <TextField
                  label="Score minimum (0–100) *"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={form.minPatternScore}
                  onChange={(e) => setForm((f) => ({ ...f, minPatternScore: e.target.value }))}
                  error={!!formError && formError.includes('score minimum')}
                  fullWidth
                />

                <TextField
                  label="Score maximum (0–100)"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={form.maxPatternScore}
                  onChange={(e) => setForm((f) => ({ ...f, maxPatternScore: e.target.value }))}
                  error={!!formError && formError.includes('score maximum')}
                  helperText="Laisser vide = aucune limite haute."
                  fullWidth
                />
              </>
            )}

            <FormControlLabel
              control={<Checkbox checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />}
              label="Enabled"
            />

            {formError ? <Alert severity="error">{formError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button variant="contained" startIcon={<SendRoundedIcon />} onClick={submit}
            sx={{ bgcolor: '#17323c', '&:hover': { bgcolor: '#0f2329' } }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Supprimer la règle</DialogTitle>
        <DialogContent>
          <Alert severity={deleteTarget?.isLast ? 'warning' : 'info'}>
            {deleteTarget?.isLast
              ? 'Aucune règle active ne restera pour cette destination. Les nouvelles publications Telegram vers cette destination seront bloquées.'
              : 'Cette règle sera définitivement supprimée.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
};

export default TelegramPublicationRulesAdminPage;
